import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Crown, Lock, BookOpen, Download, ArrowRight } from "lucide-react";

export default function Blog() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [resources, setResources] = useState(null); // null unknown, [] none/denied, [..] vip
  const [vipDenied, setVipDenied] = useState(false);

  useEffect(() => {
    api.get("/blog").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    if (!user) { setResources(null); return; }
    api.get("/vip/resources").then(({ data }) => setResources(data.resources)).catch((e) => {
      if (e.response?.status === 403) setVipDenied(true);
      setResources([]);
    });
  }, [user]);

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary"><BookOpen className="mr-2 inline h-3.5 w-3.5" /> Blog & Cost Guides</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">Build smarter, spend wiser</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">Worldwide pricing guides, product deep-dives and insider playbooks. Some premium guides are VIP-only.</p>

        {/* VIP resources hub */}
        {resources && resources.length > 0 && (
          <div className="mt-8 border border-border border-l-2 border-l-primary bg-secondary/20 p-6" data-testid="vip-resources">
            <p className="flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-primary"><Crown className="h-4 w-4" /> VIP Resources</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {resources.map((r) => (
                <a key={r.id} href={r.url} download data-testid={`resource-${r.id}`}
                  className="group flex flex-col justify-between border border-border p-4 transition-colors hover:border-primary">
                  <div>
                    <span className="font-mono2 text-[10px] uppercase tracking-widest text-primary">{r.type}</span>
                    <h3 className="mt-1 font-heading text-base font-semibold">{r.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{r.desc}</p>
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground group-hover:text-primary"><Download className="h-3.5 w-3.5" /> Download</span>
                </a>
              ))}
            </div>
          </div>
        )}
        {(vipDenied || (user && resources && resources.length === 0)) && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border border-border bg-secondary/10 p-5" data-testid="vip-teaser">
            <p className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground"><Crown className="mr-2 inline h-4 w-4 text-primary" /> VIP members unlock downloadable cost templates & premium guides</p>
            <Link to="/billing"><Button size="sm" className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black">Go VIP</Button></Link>
          </div>
        )}

        {/* Posts */}
        {posts === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-testid="blog-list">
            {posts.map((p, i) => (
              <motion.article key={p.post_id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="group flex flex-col overflow-hidden border border-border">
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="flex h-full flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#050a16]">
                    {p.cover_url && <img src={p.cover_url} alt={p.title} className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />}
                    {p.vip_only && (
                      <span data-testid="vip-lock" className="absolute left-3 top-3 inline-flex items-center gap-1 border border-primary bg-background/80 px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-primary backdrop-blur">
                        {p.locked ? <Lock className="h-3 w-3" /> : <Crown className="h-3 w-3" />} VIP
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h2 className="font-heading text-lg font-semibold leading-snug">{p.title}</h2>
                    <p className="mt-2 flex-1 text-sm text-muted-foreground">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1 font-mono2 text-[11px] uppercase tracking-widest text-primary">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
